import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, BadgeCheck, Users, Award, ThumbsUp, PenLine, ImagePlus, Loader2, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import Seo from "@/components/common/Seo";
import Reveal from "@/components/common/Reveal";
import RatingStars from "@/components/common/RatingStars";
import InitialsAvatar from "@/components/common/InitialsAvatar";
import { TrustedMarquee } from "@/components/layout/TrustedMarquee";
import { formatDate } from "@/lib/format";
import { stagger, fadeUp } from "@/lib/motion";

const TRUST = [
  { icon: Users, label: "Trusted by 10,000+ Customers" },
  { icon: Award, label: "Premium Quality Products" },
  { icon: ThumbsUp, label: "Performance You Can Trust" },
  { icon: BadgeCheck, label: "Loved by Car Enthusiasts" },
];

function WriteReviewDialog({ open, onOpenChange, products, onSubmitted }) {
  const user = null; // accounts handled by Shopify; web reviews are submitted by name/email
  const [form, setForm] = useState({ name: user?.name || "", productSlug: "", rating: 5, content: "" });
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const onPickPhotos = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 4 - photos.length);
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const f of files) {
        const res = await api.uploadImage(f);
        urls.push(`${API}/images/${res.id}`);
      }
      setPhotos((p) => [...p, ...urls]);
    } catch {
      toast.error("Could not upload photo. Try a smaller image.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.content.trim()) {
      toast.error("Please fill in your name and review");
      return;
    }
    setSubmitting(true);
    try {
      await api.createReview({ ...form, photos, email: user?.email });
      toast.success("Thank you! Your review was submitted.");
      onOpenChange(false);
      setForm({ name: user?.name || "", productSlug: "", rating: 5, content: "" });
      setPhotos([]);
      onSubmitted();
    } catch (err) {
      toast.error("Could not submit review. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto border-[color:var(--acx-border)] bg-[var(--acx-card)] text-white">
        <DialogHeader><DialogTitle className="text-white">Write a Review</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="write-review-form">
          <div>
            <label className="mb-1.5 block text-[13px] text-white/70">Your Name</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="review-name-input" className="w-full rounded-[12px] border border-[color:var(--acx-border)] bg-[var(--acx-bg-2)] px-4 py-3 text-[14px] text-white placeholder:text-white/35 focus:border-white/30 focus:outline-none" placeholder="Full name" />
          </div>
          <div>
            <label className="mb-1.5 block text-[13px] text-white/70">Product</label>
            <Select value={form.productSlug} onValueChange={(v) => setForm({ ...form, productSlug: v })}>
              <SelectTrigger data-testid="review-product-select" className="border-[color:var(--acx-border)] bg-[var(--acx-bg-2)] text-white"><SelectValue placeholder="Select a product" /></SelectTrigger>
              <SelectContent className="max-h-60 border-[color:var(--acx-border)] bg-[var(--acx-card)] text-white">
                {products.map((p) => (<SelectItem key={p.slug} value={p.slug}>{p.name}</SelectItem>))}
              </SelectContent>
            </Select>
            {user?.email && form.productSlug && (
              <p className="mt-1.5 text-[12px] text-white/40">If your account has purchased this product, your review will be marked <span className="text-[#34D399]">Verified Purchase</span>.</p>
            )}
          </div>
          <div>
            <label className="mb-1.5 block text-[13px] text-white/70">Rating</label>
            <div className="flex gap-1.5">
              {[1, 2, 3, 4, 5].map((n) => (
                <button type="button" key={n} onClick={() => setForm({ ...form, rating: n })} aria-label={`${n} stars`}>
                  <Star size={26} className={n <= form.rating ? "fill-[#E11B22] text-[#E11B22]" : "text-white/25"} />
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="mb-1.5 block text-[13px] text-white/70">Your Review</label>
            <textarea rows={4} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} data-testid="review-content-input" className="w-full rounded-[12px] border border-[color:var(--acx-border)] bg-[var(--acx-bg-2)] px-4 py-3 text-[14px] text-white placeholder:text-white/35 focus:border-white/30 focus:outline-none" placeholder="Share your experience..." />
          </div>
          <div>
            <label className="mb-1.5 block text-[13px] text-white/70">Add Photos <span className="text-white/40">(optional, up to 4)</span></label>
            <div className="flex flex-wrap items-center gap-2">
              {photos.map((src, i) => (
                <div key={src} className="relative h-16 w-16 overflow-hidden rounded-[10px] border border-[color:var(--acx-border)]">
                  <img src={src} alt="Upload preview" className="h-full w-full object-cover" />
                  <button type="button" onClick={() => setPhotos((p) => p.filter((_, idx) => idx !== i))} className="absolute right-0.5 top-0.5 rounded-full bg-black/70 p-0.5 text-white" aria-label="Remove photo"><X size={11} /></button>
                </div>
              ))}
              {photos.length < 4 && (
                <label data-testid="review-photo-upload" className="flex h-16 w-16 cursor-pointer flex-col items-center justify-center gap-1 rounded-[10px] border border-dashed border-[color:var(--acx-border)] text-white/50 transition-colors hover:border-white/30 hover:text-white">
                  {uploading ? <Loader2 size={16} className="animate-spin" /> : <ImagePlus size={16} />}
                  <span className="text-[10px]">Add</span>
                  <input type="file" accept="image/*" multiple className="hidden" onChange={onPickPhotos} disabled={uploading} />
                </label>
              )}
            </div>
          </div>
          <button type="submit" disabled={submitting || uploading} data-testid="review-submit-button" className="w-full rounded-[12px] bg-[#E11B22] px-5 py-3.5 text-[14px] font-semibold text-white transition-colors hover:bg-[#c9151b] disabled:opacity-60">
            {submitting ? "Submitting..." : "Submit Review"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Reviews() {
  const [summary, setSummary] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [total, setTotal] = useState(0);
  const [products, setProducts] = useState([]);
  const [filterProduct, setFilterProduct] = useState("all");
  const [filterRating, setFilterRating] = useState("0");
  const [sort, setSort] = useState("recent");
  const [limit, setLimit] = useState(9);
  const [writeOpen, setWriteOpen] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  const loadReviews = useCallback((lim) => {
    const l = lim ?? limit;
    api.getReviews({ product: filterProduct, rating: Number(filterRating), sort, limit: l }).then((d) => {
      setReviews(d.items);
      setTotal(d.total);
    }).catch(() => {});
  }, [filterProduct, filterRating, sort, limit]);

  useEffect(() => {
    api.getReviewsSummary().then(setSummary).catch(() => {});
    api.getProducts({ limit: 60 }).then((d) => setProducts(d.items)).catch(() => {});
  }, []);

  useEffect(() => {
    loadReviews(limit);
  }, [loadReviews, limit]);

  const refresh = () => { api.getReviewsSummary().then(setSummary); loadReviews(limit); };

  return (
    <div className="bg-[var(--acx-bg)]">
      <Seo title="Product Reviews" description="Read verified AutoCareX reviews from real car owners and detailers. See ratings, breakdowns and customer experiences." />

      {/* Hero */}
      <section className="relative acx-noise overflow-hidden border-b border-[color:var(--acx-border)]">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(800px_circle_at_50%_0%,rgba(225,27,34,0.10),transparent_60%)]" />
        <div className="acx-container relative px-4 py-16 text-center sm:px-6 lg:px-10 lg:py-24">
          <Reveal>
            <span className="text-[12px] uppercase tracking-[0.22em] text-white/50">Customer Reviews</span>
            <h1 className="mt-3 font-display text-[40px] font-extrabold leading-[1.04] tracking-[-0.03em] sm:text-6xl">Product Reviews</h1>
            <p className="mx-auto mt-5 max-w-xl text-[17px] leading-relaxed text-white/65">
              Trusted by thousands. See what real customers think about AutoCareX performance.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Trusted by marquee */}
      <TrustedMarquee />

      {/* Summary */}
      {summary && (
        <section className="border-b border-[color:var(--acx-border)] bg-[var(--acx-bg-2)]">
          <div className="acx-container grid grid-cols-1 gap-8 px-4 py-14 sm:px-6 lg:grid-cols-3 lg:px-10">
            <div className="flex flex-col items-center justify-center rounded-[18px] border border-[color:var(--acx-border)] bg-[var(--acx-card)] p-8 text-center" data-testid="reviews-overall-rating">
              <p className="font-display text-6xl font-extrabold">{summary.average}</p>
              <RatingStars value={summary.average} size={20} className="mt-3" />
              <p className="mt-3 text-[14px] text-white/55">Based on {summary.total} reviews</p>
            </div>
            <div className="rounded-[18px] border border-[color:var(--acx-border)] bg-[var(--acx-card)] p-8" data-testid="rating-breakdown">
              <h3 className="mb-4 text-[15px] font-semibold">Rating Breakdown</h3>
              {[5, 4, 3, 2, 1].map((s) => (
                <div key={s} className="mb-2.5 flex items-center gap-3">
                  <span className="flex w-10 items-center gap-1 text-[13px] text-white/60">{s}<Star size={12} className="fill-white/40 text-white/40" /></span>
                  <div className="h-2 flex-1 overflow-hidden rounded-full bg-white/10">
                    <motion.div initial={{ width: 0 }} whileInView={{ width: `${summary.breakdownPercent[s] || 0}%` }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="h-full rounded-full bg-[#E11B22]" />
                  </div>
                  <span className="w-10 text-right text-[12px] text-white/45">{summary.breakdownPercent[s] || 0}%</span>
                </div>
              ))}
            </div>
            <div className="flex flex-col justify-center gap-3 rounded-[18px] border border-[color:var(--acx-border)] bg-[var(--acx-card)] p-8">
              {TRUST.map((t) => (
                <div key={t.label} className="flex items-center gap-3">
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/5 text-white/80"><t.icon size={16} /></span>
                  <span className="text-[13px] text-white/75">{t.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Reviews + Sidebar */}
      <section className="bg-[var(--acx-bg)]">
        <div className="acx-container grid grid-cols-1 gap-10 px-4 py-14 sm:px-6 lg:grid-cols-12 lg:px-10">
          <div className="lg:col-span-8">
            {/* Filters */}
            <div className="mb-6 flex flex-wrap items-center gap-3">
              <Select value={filterProduct} onValueChange={(v) => { setFilterProduct(v); setLimit(9); }}>
                <SelectTrigger data-testid="reviews-filter-product" className="w-[190px] border-[color:var(--acx-border)] bg-[var(--acx-card)] text-white"><SelectValue placeholder="All Products" /></SelectTrigger>
                <SelectContent className="max-h-60 border-[color:var(--acx-border)] bg-[var(--acx-card)] text-white">
                  <SelectItem value="all">All Products</SelectItem>
                  {products.map((p) => (<SelectItem key={p.slug} value={p.slug}>{p.name}</SelectItem>))}
                </SelectContent>
              </Select>
              <Select value={filterRating} onValueChange={(v) => { setFilterRating(v); setLimit(9); }}>
                <SelectTrigger data-testid="reviews-filter-rating" className="w-[150px] border-[color:var(--acx-border)] bg-[var(--acx-card)] text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="border-[color:var(--acx-border)] bg-[var(--acx-card)] text-white">
                  <SelectItem value="0">All Ratings</SelectItem>
                  {[5, 4, 3, 2, 1].map((r) => (<SelectItem key={r} value={String(r)}>{r} Stars</SelectItem>))}
                </SelectContent>
              </Select>
              <Select value={sort} onValueChange={(v) => { setSort(v); setLimit(9); }}>
                <SelectTrigger data-testid="reviews-sort" className="w-[160px] border-[color:var(--acx-border)] bg-[var(--acx-card)] text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="border-[color:var(--acx-border)] bg-[var(--acx-card)] text-white">
                  <SelectItem value="recent">Most Recent</SelectItem>
                  <SelectItem value="highest">Highest Rated</SelectItem>
                  <SelectItem value="lowest">Lowest Rated</SelectItem>
                </SelectContent>
              </Select>
              <button onClick={() => setWriteOpen(true)} data-testid="reviews-write-review-button" className="ml-auto inline-flex items-center gap-2 rounded-[12px] bg-[#E11B22] px-4 py-2.5 text-[14px] font-semibold text-white transition-colors hover:bg-[#c9151b]">
                <PenLine size={15} /> Write a Review
              </button>
            </div>

            {/* Verification explainer */}
            <div className="mb-4 flex items-start gap-2 rounded-[12px] border border-[color:var(--acx-border)] bg-[var(--acx-card)] px-3.5 py-3 text-[12.5px] leading-relaxed text-white/55" data-testid="reviews-verified-explainer">
              <BadgeCheck size={16} className="mt-0.5 shrink-0 text-[#34D399]" />
              <span>
                <span className="font-semibold text-white/75">Verified Purchase</span> means the reviewer's account has actually bought this product from AutoCareX. Photos are uploaded by real customers.
              </span>
            </div>

            {/* List */}
            <motion.div initial="hidden" animate="show" variants={stagger} className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="reviews-list">
              {reviews.map((r) => (
                <motion.div key={r.id} variants={fadeUp} data-testid="review-card" className="rounded-[16px] border border-[color:var(--acx-border)] bg-[var(--acx-card)] p-5">
                  <div className="flex items-center gap-3">
                    <InitialsAvatar name={r.name} size={40} />
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-[14px] font-semibold text-white">{r.name}</p>
                        {r.verified && <span className="inline-flex items-center gap-1 rounded-full bg-[#34D399]/15 px-2 py-0.5 text-[10px] font-medium text-[#34D399]"><BadgeCheck size={11} /> Verified Purchase</span>}
                      </div>
                      <RatingStars value={r.rating} size={13} className="mt-1" />
                    </div>
                  </div>
                  <p className="mt-3 text-[14px] leading-relaxed text-white/75">{r.content}</p>
                  {r.photos?.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2" data-testid="review-photos">
                      {r.photos.map((src) => (
                        <button key={src} onClick={() => setLightbox(src)} className="h-16 w-16 overflow-hidden rounded-[8px] border border-[color:var(--acx-border)] transition-transform hover:scale-[1.04]">
                          <img src={src} alt="Customer review" className="h-full w-full object-cover" loading="lazy" />
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="mt-4 flex items-center justify-between border-t border-[color:var(--acx-border)] pt-3">
                    {r.productSlug ? (
                      <Link to={`/product/${r.productSlug}`} className="text-[12px] text-white/55 hover:text-white">{r.productName}</Link>
                    ) : (<span className="text-[12px] text-white/55">{r.productName}</span>)}
                    <span className="text-[12px] text-white/40">{formatDate(r.createdAt)}</span>
                  </div>
                </motion.div>
              ))}
            </motion.div>
            {reviews.length === 0 && (
              <div className="rounded-[16px] border border-[color:var(--acx-border)] bg-[var(--acx-card)] py-16 text-center text-white/55">No reviews match your filters yet.</div>
            )}
            {reviews.length < total && (
              <div className="mt-8 text-center">
                <button onClick={() => setLimit((l) => l + 6)} data-testid="reviews-load-more" className="inline-flex items-center gap-2 rounded-[12px] border border-[color:var(--acx-border)] bg-white/5 px-6 py-3 text-[14px] font-semibold text-white transition-colors hover:bg-white/10">
                  Load More Reviews
                </button>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-4">
            <div className="sticky top-28 space-y-4">
              <div className="rounded-[18px] border border-[color:var(--acx-border)] bg-[var(--acx-card)] p-6">
                <h3 className="mb-4 text-[15px] font-semibold">Most Reviewed Products</h3>
                <div className="space-y-3">
                  {summary?.mostReviewed?.map((p) => (
                    <Link key={p.slug} to={`/product/${p.slug}`} className="flex items-center gap-3 rounded-[12px] border border-transparent p-2 transition-colors hover:border-[color:var(--acx-border)] hover:bg-white/5">
                      <img src={p.image} alt={p.name} className="h-12 w-12 rounded-[10px] object-cover" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[13px] font-medium text-white">{p.name}</p>
                        <div className="mt-0.5 flex items-center gap-1.5"><RatingStars value={p.rating} size={11} /><span className="text-[11px] text-white/45">({p.reviewCount})</span></div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
              <div className="rounded-[18px] border border-[color:var(--acx-border)] bg-[var(--acx-card)] p-6 text-center">
                <h3 className="font-display text-lg font-bold">Share your experience</h3>
                <p className="mt-2 text-[13px] text-white/55">Help others choose with confidence.</p>
                <button onClick={() => setWriteOpen(true)} className="mt-4 inline-flex items-center gap-2 rounded-[12px] bg-white px-5 py-3 text-[14px] font-semibold text-black transition-colors hover:bg-white/90">
                  <PenLine size={15} /> Write a Review
                </button>
              </div>
            </div>
          </aside>
        </div>
      </section>

      <WriteReviewDialog open={writeOpen} onOpenChange={setWriteOpen} products={products} onSubmitted={refresh} />

      <Dialog open={!!lightbox} onOpenChange={(o) => !o && setLightbox(null)}>
        <DialogContent className="max-w-2xl border-[color:var(--acx-border)] bg-[var(--acx-card)] p-2">
          {lightbox && <img src={lightbox} alt="Customer review" className="max-h-[80vh] w-full rounded-[10px] object-contain" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
